
import Vue from "vue";
import CatIcon from "@/components/atoms/CatIcon.vue";
import WriterTypeIndicatorSkipDialog from "@/components/ui/WriterTypeIndicatorSkipDialog.vue";
import type { AssistantCatMaster } from "@/lib/models/assistantCat";
import { FeedbackManuscriptMode } from "@/lib/models/assist";
import { NolaAnalytics, NolaItemId } from "@/lib/utils/analytics";
import normalModeIcon from "@/assets/img/icon/mode/normal.svg";
import dryModeIcon from "@/assets/img/icon/mode/dry.svg";

const MODE_CONFIG = [
  {
    mode: FeedbackManuscriptMode.NORMAL,
    icon: normalModeIcon,
    titleKey: "myPage.assistantCat.modeNames.normal",
    descriptionLine1Key: "myPage.feedbackManuscriptModeDialog.normalModeDescriptionLine1",
    descriptionLine2Key: "myPage.feedbackManuscriptModeDialog.normalModeDescriptionLine2",
  },
  {
    mode: FeedbackManuscriptMode.DRY,
    icon: dryModeIcon,
    titleKey: "myPage.assistantCat.modeNames.dry",
    descriptionLine1Key: "myPage.feedbackManuscriptModeDialog.dryModeDescriptionLine1",
    descriptionLine2Key: "myPage.feedbackManuscriptModeDialog.dryModeDescriptionLine2",
  },
] as const;

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    CatIcon,
    WriterTypeIndicatorSkipDialog,
  },
  props: {
    assistantCatMaster: {
      type: Object as () => AssistantCatMaster | null,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      error: null as string | null,
      showSkipDialog: false,
      nolaAnalytics: new NolaAnalytics(this.$gtm, this.$store),
    };
  },
  computed: {
    currentMode(): FeedbackManuscriptMode {
      return this.assistantCatMaster?.feedbackManuscriptMode ?? FeedbackManuscriptMode.NORMAL;
    },
    dryModeExtraFish(): number {
      return this.$store.getters["assistantCatModule/dryModeExtraFish"];
    },
    modeOptions() {
      return MODE_CONFIG.map((config) => ({
        ...config,
        icon: config.icon,
        title: this.$t(config.titleKey) as string,
        descriptionLine1: this.$t(config.descriptionLine1Key) as string,
        descriptionLine2: this.$t(config.descriptionLine2Key) as string,
      }));
    },
    formattedDryModeExtraFish(): string {
      const locale = this.$i18n?.locale ?? "ja-JP";
      return this.dryModeExtraFish.toLocaleString(locale);
    },
    closeButtonAriaLabel(): string {
      return this.$t("myPage.feedbackManuscriptModeDialog.closeAriaLabel") as string;
    },
  },
  mounted() {
    this.loadDryModeExtraFish();
  },
  methods: {
    costLabelText(mode: FeedbackManuscriptMode): string {
      if (mode === FeedbackManuscriptMode.DRY) {
        const fishAmount = this.dryModeExtraFish;
        const locale = this.$i18n?.locale ?? "ja-JP";
        const formattedAmount = fishAmount.toLocaleString(locale);

        return this.$t("myPage.feedbackManuscriptModeDialog.dryModeCostLabel", {
          extraFish: formattedAmount,
        }) as string;
      }

      return this.$t("myPage.feedbackManuscriptModeDialog.normalModeCostLabel") as string;
    },
    handleClose() {
      this.error = null;
      this.$emit("close", true);
    },
    handleRetry() {
      this.error = null;
    },
    async handleChangeMode(mode: FeedbackManuscriptMode) {
      if (this.currentMode === mode) {
        return;
      }

      if (!this.assistantCatMaster) {
        this.showSkipDialog = true;
        return;
      }

      this.isLoading = true;
      try {
        // APIを呼び出して選択状態を更新・Vuexストアに選択状態をキャッシュ
        await this.$store.dispatch("assistantCatModule/updateAssistantCatMaster", {
          feedbackManuscriptMode: mode,
        });

        this.nolaAnalytics.logButtonEvent(NolaItemId.AssistantCatChangeFeedbackManuscriptMode, mode);
        this.$notify({
          title: this.$t("myPage.feedbackManuscriptModeDialog.successMessage") as string,
          type: "success",
        });
        this.handleClose();
      } catch (error) {
        console.error("Failed to change assistant cat mode:", error);
        this.error = this.$t("myPage.feedbackManuscriptModeDialog.errorMessage") as string;
        this.$notify({
          title: this.$t("myPage.feedbackManuscriptModeDialog.errorMessage") as string,
          type: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async loadDryModeExtraFish() {
      try {
        await this.$store.dispatch("assistantCatModule/fetchDryModeExtraFish");
      } catch (error) {
        console.error("fetchDryModeExtraFish failed:", error);
      }
    },
    onClickWriterTypeIndicator() {
      const baseUrl = process.env.VUE_APP_NOLA_WEB;
      const writerTypeUrl = `${baseUrl}/writer-type-indicator/`;
      window.open(writerTypeUrl, "_blank");
    },
  },
});

interface Props {
  assistantCatMaster: AssistantCatMaster | null;
}

interface Data {
  isLoading: boolean;
  error: string | null;
  showSkipDialog: boolean;
  nolaAnalytics: NolaAnalytics;
}

interface Computed {
  currentMode: FeedbackManuscriptMode;
  dryModeExtraFish: number;
  formattedDryModeExtraFish: string;
  modeOptions: Array<{
    mode: FeedbackManuscriptMode;
    icon: string;
    title: string;
    descriptionLine1: string;
    descriptionLine2: string;
  }>;
  closeButtonAriaLabel: string;
}

interface Methods {
  costLabelText: (mode: FeedbackManuscriptMode) => string;
  handleClose: () => void;
  handleRetry: () => void;
  handleChangeMode: (mode: FeedbackManuscriptMode) => void;
  loadDryModeExtraFish: () => Promise<void>;
  onClickWriterTypeIndicator: () => void;
}
